"use client";

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/language-context";
import { useRequestLanguage } from "@/components/providers/RequestLanguageProvider";
import { Globe } from "lucide-react";
import { cn } from "@/lib/utils";

const languages = [
    { code: "en", name: "English" },
    { code: "es", name: "Español" },
];

export function FloatingLanguageSelector() {
    const languageContext = useLanguage(false);
    const requestLanguageContext = useRequestLanguage(false);
    const { toast } = useToast();

    const t = (
        languageContext ||
        requestLanguageContext || { t: (key: string) => key }
    ).t;

    const language =
        languageContext?.language || requestLanguageContext?.language || "en";
    const setLanguage =
        languageContext?.setLanguage ||
        requestLanguageContext?.setLanguage ||
        (() => {});

    const handleLanguageChange = async (value: string) => {
        try {
            await setLanguage(value);
            toast({
                title: t("errors.languageUpdated"),
            });
        } catch (error) {
            console.error(error);
            toast({
                title: t("errors.languageUpdateFailed"),
                variant: "destructive",
            });
        }
    };

    return (
        <div className="fixed bottom-4 left-4 z-50">
            <Select value={language} onValueChange={handleLanguageChange}>
                <SelectTrigger
                    hideIcon
                    className={cn(
                        "flex items-center gap-2 rounded-full px-3 py-2",
                        "bg-white dark:bg-gray-800 shadow-lg",
                        "border border-gray-200 dark:border-gray-700",
                        "hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    )}
                >
                    <Globe className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                    <span className="text-sm font-medium">
                        {languages.find((lang) => lang.code === language)?.name}
                    </span>
                </SelectTrigger>
                <SelectContent align="end" className="w-[120px]">
                    {languages.map((lang) => (
                        <SelectItem
                            key={lang.code}
                            value={lang.code}
                            className="cursor-pointer"
                        >
                            {lang.name}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );
}
